import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Phone, Mail, Truck, Clock, CircleDot } from 'lucide-react';

interface Transporter {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  bio?: string;
  species_handled?: string[];
  vehicle_type?: string;
  availability?: string;
  city: string;
  state_code: string;
  service_radius_miles?: number;
  is_verified?: boolean;
}

interface TransporterCardProps {
  transporter: Transporter;
  isSelected?: boolean;
  onClick?: () => void;
}

export const TransporterCard = ({ transporter, isSelected, onClick }: TransporterCardProps) => {
  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'ring-2 ring-primary border-primary' : ''
      }`}
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <CardTitle className="text-base font-medium">{transporter.name}</CardTitle>
          {transporter.is_verified && (
            <Badge variant="secondary" className="text-xs bg-green-500/10 text-green-600">
              Verified
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {transporter.bio && (
          <p className="text-sm text-muted-foreground line-clamp-2">{transporter.bio}</p>
        )}
        
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" />
          <span>{transporter.city}, {transporter.state_code}</span>
        </div>
        
        {transporter.service_radius_miles && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <CircleDot className="h-3.5 w-3.5" />
            <span>{transporter.service_radius_miles} mile radius</span>
          </div>
        )}
        
        {transporter.vehicle_type && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Truck className="h-3.5 w-3.5" />
            <span>{transporter.vehicle_type}</span>
          </div>
        )}
        
        {transporter.availability && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            <span>{transporter.availability}</span>
          </div>
        )}
        
        {transporter.species_handled && transporter.species_handled.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {transporter.species_handled.slice(0, 4).map((species) => (
              <Badge key={species} variant="outline" className="text-xs">
                {species}
              </Badge>
            ))}
            {transporter.species_handled.length > 4 && (
              <Badge variant="outline" className="text-xs">
                +{transporter.species_handled.length - 4} more
              </Badge>
            )}
          </div>
        )}
        
        <div className="flex gap-3 pt-2 border-t">
          {transporter.phone && (
            <a 
              href={`tel:${transporter.phone}`} 
              className="flex items-center gap-1 text-xs text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <Phone className="h-3 w-3" />
              Call
            </a>
          )}
          {transporter.email && (
            <a 
              href={`mailto:${transporter.email}`} 
              className="flex items-center gap-1 text-xs text-primary hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <Mail className="h-3 w-3" />
              Email
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
